// src/pages/Invoices/Report/ProfitReportPage.jsx
// ======================================================================
// صفحة موحّدة لتقارير الأرباح الثلاثة — كل تقرير يحدَّد بـ config:
//   INVOICE_PROFIT        أرباح الفواتير          /ProfitReports/by-invoice
//   ITEMS_PROFIT_SUMMARY  أرباح الأصناف تجميعي     /ProfitReports/items-summary
//   ITEMS_PROFIT_DETAIL   أرباح الأصناف تفصيلي     /ProfitReports/items-detail
// ======================================================================
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Autocomplete, TextField, Typography, Alert } from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PriceCheckOutlinedIcon from '@mui/icons-material/PriceCheckOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import PercentIcon from '@mui/icons-material/Percent';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import GridOnIcon from '@mui/icons-material/GridOn';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

import axios from '../../../axios';
import {
  ReportPage, ReportFilters, ReportSelect, ReportTableCard,
  useReportDateRange, useAllowedBranches, reportGridSx, fieldSx, fmtMoney, fmtNumber,
} from '../../../components/Reports/ReportKit';
import {
  COST_METHODS, PROFIT_FILTERS, applyProfitFilter, costMethodLabel,
  ProfitCell, MarginCell, WarningsCell, CostSourceCell, CostingInfo,
} from '../../../components/Reports/ProfitKit';

// ───────────────────────── خلايا مساعدة ─────────────────────────
const money = (color, bold = 700) => (p) => (
  <Typography sx={{ direction: 'ltr', fontWeight: bold, fontSize: 13.5, color: Number(p.value) < 0 ? '#be123c' : color }}>
    {fmtMoney(p.value)}
  </Typography>
);
const qtyCell = (p) => (
  <Typography sx={{ direction: 'ltr', fontWeight: 700, fontSize: 13.5, color: Number(p.value) < 0 ? '#be123c' : '#334155' }}>
    {fmtNumber(p.value)}
  </Typography>
);
const dateCell = (p) => <Typography sx={{ direction: 'ltr', fontSize: 13, color: '#475569' }}>{p.value ? dayjs(p.value).format('YYYY-MM-DD') : '—'}</Typography>;
const typeCell = (p) => {
  const ret = p.row.invoiceType === 3;
  return (
    <Typography sx={{ px: 1, py: .25, borderRadius: '999px', fontSize: 12, fontWeight: 800, bgcolor: ret ? '#ffe4e6' : '#eef2ff', color: ret ? '#be123c' : '#4338ca' }}>
      {p.value}
    </Typography>
  );
};
const C = (field, headerName, width, extra = {}) => ({ field, headerName, width, headerAlign: 'center', align: 'center', ...extra });
const num = { type: 'number' };

const INVOICE_TYPES = [
  { value: '1', label: 'مبيعات' },
  { value: '11', label: 'نقاط بيع' },
  { value: '3', label: 'مرتجع مبيعات' },
];

// ───────────────────────── إعدادات التقارير ─────────────────────────
export const INVOICE_PROFIT = {
  key: 'invoice-profit',
  title: 'أرباح الفواتير',
  subtitle: 'الإيراد والتكلفة والربح لكل فاتورة مبيعات — بعد طرح المرتجعات',
  icon: <ReceiptLongOutlinedIcon />,
  endpoint: '/ProfitReports/by-invoice',
  gridTitle: 'الفواتير',
  noRows: 'لا توجد فواتير مبيعات في هذه الفترة',
  filters: { invoiceType: true, customer: true },
  search: (r) => `${r.invoiceNumber} ${r.customerName}`,
  columns: [
    C('invoiceNumber', 'رقم الفاتورة', 120, { renderCell: p => <Typography sx={{ fontWeight: 800, fontSize: 13.5 }}>{p.value}</Typography> }),
    C('invoiceTypeName', 'النوع', 95, { renderCell: typeCell }),
    C('issueDate', 'التاريخ', 105, { renderCell: dateCell }),
    C('customerName', 'العميل', 170, { flex: 1, minWidth: 160 }),
    C('linesCount', 'الأصناف', 75, num),
    C('revenue', 'الإيراد', 120, { ...num, renderCell: money('#0f172a') }),
    C('cost', 'التكلفة', 120, { ...num, renderCell: money('#b45309') }),
    C('profit', 'الربح', 120, { ...num, renderCell: p => <ProfitCell value={p.value} /> }),
    C('margin', 'الهامش', 95, { ...num, renderCell: p => <MarginCell value={p.value} /> }),
    C('warnings', 'تنبيهات التكلفة', 190, { sortable: false, filterable: false, disableExport: true, renderCell: p => <WarningsCell row={p.row} /> }),
  ],
  excel: (r) => ({
    'رقم الفاتورة': r.invoiceNumber, 'النوع': r.invoiceTypeName, 'التاريخ': dayjs(r.issueDate).format('YYYY-MM-DD'),
    'العميل': r.customerName, 'الأصناف': r.linesCount, 'الإيراد': r.revenue, 'التكلفة': r.cost,
    'الربح': r.profit, 'الهامش %': r.margin, 'بيع على المكشوف': r.negativeStockLines, 'بدون تكلفة': r.noCostLines,
  }),
};

export const ITEMS_PROFIT_SUMMARY = {
  key: 'items-profit-summary',
  title: 'أرباح الأصناف — تجميعي',
  subtitle: 'ربح كل صنف خلال الفترة — الكميات بالوحدة الأساسية',
  icon: <Inventory2OutlinedIcon />,
  endpoint: '/ProfitReports/items-summary',
  gridTitle: 'الأصناف',
  noRows: 'لا توجد مبيعات أصناف في هذه الفترة',
  filters: { product: true, stock: true },
  search: (r) => `${r.productId} ${r.productNameAr ?? ''} ${r.productNameEn ?? ''}`,
  columns: [
    C('productId', 'الرقم', 70),
    C('productNameAr', 'الصنف', 200, { flex: 1.4, minWidth: 180, renderCell: p => <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>{p.value || p.row.productNameEn || '—'}</Typography> }),
    C('unitName', 'الوحدة', 80),
    C('netQty', 'صافي الكمية', 100, { ...num, renderCell: qtyCell }),
    C('returnedQty', 'المرتجع', 80, { ...num, renderCell: qtyCell }),
    C('avgSellPrice', 'متوسط البيع', 105, { ...num, renderCell: money('#334155', 600) }),
    C('avgUnitCost', 'متوسط التكلفة', 110, { ...num, renderCell: money('#b45309', 600) }),
    C('revenue', 'الإيراد', 120, { ...num, renderCell: money('#0f172a') }),
    C('cost', 'التكلفة', 115, { ...num, renderCell: money('#b45309') }),
    C('profit', 'الربح', 120, { ...num, renderCell: p => <ProfitCell value={p.value} /> }),
    C('margin', 'الهامش', 95, { ...num, renderCell: p => <MarginCell value={p.value} /> }),
    C('warnings', 'تنبيهات التكلفة', 190, { sortable: false, filterable: false, disableExport: true, renderCell: p => <WarningsCell row={p.row} /> }),
  ],
  excel: (r) => ({
    'الرقم': r.productId, 'الصنف': r.productNameAr || r.productNameEn, 'الوحدة': r.unitName,
    'الكمية المباعة': r.soldQty, 'المرتجع': r.returnedQty, 'صافي الكمية': r.netQty,
    'متوسط البيع': r.avgSellPrice, 'متوسط التكلفة': r.avgUnitCost,
    'الإيراد': r.revenue, 'التكلفة': r.cost, 'الربح': r.profit, 'الهامش %': r.margin,
    'بيع على المكشوف': r.negativeStockLines, 'بدون تكلفة': r.noCostLines,
  }),
};

export const ITEMS_PROFIT_DETAIL = {
  key: 'items-profit-detail',
  title: 'أرباح الأصناف — تفصيلي',
  subtitle: 'ربح كل صنف في كل فاتورة مع مصدر التكلفة',
  icon: <ListAltOutlinedIcon />,
  endpoint: '/ProfitReports/items-detail',
  gridTitle: 'سطور المبيعات',
  noRows: 'لا توجد مبيعات في هذه الفترة',
  filters: { invoiceType: true, product: true, customer: true, stock: true },
  search: (r) => `${r.invoiceNumber} ${r.customerName} ${r.productNameAr ?? ''} ${r.productNameEn ?? ''}`,
  columns: [
    C('invoiceNumber', 'الفاتورة', 110, { renderCell: p => <Typography sx={{ fontWeight: 800, fontSize: 13.5 }}>{p.value}</Typography> }),
    C('invoiceTypeName', 'النوع', 90, { renderCell: typeCell }),
    C('issueDate', 'التاريخ', 100, { renderCell: dateCell }),
    C('customerName', 'العميل', 140),
    C('productNameAr', 'الصنف', 180, { flex: 1.3, minWidth: 170, renderCell: p => <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>{p.value || p.row.productNameEn || '—'}</Typography> }),
    C('unitName', 'الوحدة', 75),
    C('quantity', 'الكمية', 80, { ...num, renderCell: qtyCell }),
    C('unitPrice', 'سعر البيع', 100, { ...num, renderCell: money('#334155', 600) }),
    C('unitCost', 'تكلفة الوحدة', 105, { ...num, renderCell: money('#b45309', 600) }),
    C('revenue', 'الإيراد', 110, { ...num, renderCell: money('#0f172a') }),
    C('cost', 'التكلفة', 110, { ...num, renderCell: money('#b45309') }),
    C('profit', 'الربح', 110, { ...num, renderCell: p => <ProfitCell value={p.value} /> }),
    C('margin', 'الهامش', 90, { ...num, renderCell: p => <MarginCell value={p.value} /> }),
    C('costSourceName', 'مصدر التكلفة', 150, { renderCell: p => <CostSourceCell row={p.row} /> }),
  ],
  excel: (r) => ({
    'الفاتورة': r.invoiceNumber, 'النوع': r.invoiceTypeName, 'التاريخ': dayjs(r.issueDate).format('YYYY-MM-DD'),
    'العميل': r.customerName, 'الصنف': r.productNameAr || r.productNameEn, 'الوحدة': r.unitName,
    'الكمية': r.quantity, 'سعر البيع': r.unitPrice, 'تكلفة الوحدة': r.unitCost,
    'الإيراد': r.revenue, 'التكلفة': r.cost, 'الربح': r.profit, 'الهامش %': r.margin,
    'مصدر التكلفة': r.costSourceName, 'بيع على المكشوف': r.negativeStock ? 'نعم' : '',
  }),
};

// ======================================================================
export default function ProfitReportPage({ config }) {
  const companyId = parseInt(localStorage.getItem('companyId'), 10);
  const range = useReportDateRange('today');
  const { branchOptions, defaultBranch, ready, branchName } = useAllowedBranches(companyId);

  const [branchId, setBranchId] = useState('');
  const [costMethod, setCostMethod] = useState('avg');
  const [invoiceType, setInvoiceType] = useState('');
  const [stockId, setStockId] = useState('');
  const [product, setProduct] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [profitFilter, setProfitFilter] = useState('');
  const [search, setSearch] = useState('');

  const [stocks, setStocks] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [data, setData] = useState({ rows: [], summary: null, truncated: false });
  const [loading, setLoading] = useState(false);

  const f = config.filters;

  useEffect(() => {
    if (!companyId) return;
    if (f.stock) axios.get(`/Stock/by-company/${companyId}`).then(r => setStocks(r.data || [])).catch(() => {});
    if (f.product) axios.get(`/Product/by-company/${companyId}`).then(r => setProducts(r.data || [])).catch(() => {});
    if (f.customer) axios.get(`/Customer/by-company/${companyId}`).then(r => setCustomers(r.data || [])).catch(() => {});
  }, [companyId, f.stock, f.product, f.customer]);

  useEffect(() => { if (ready) setBranchId(defaultBranch); }, [ready, defaultBranch]);

  const fetchReport = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await axios.get(config.endpoint, {
        params: {
          companyId,
          dateFrom: range.dateFrom,
          dateTo: range.dateTo,
          branchId: branchId ? Number(branchId) : null,
          costMethod,
          ...(f.invoiceType && { invoiceType: invoiceType ? Number(invoiceType) : null }),
          ...(f.stock && { stockId: stockId ? Number(stockId) : null }),
          ...(f.product && { productId: product?.productId || product?.id || null }),
          ...(f.customer && { customerId: customer?.id || null }),
        },
      });
      setData({
        rows: Array.isArray(res.data?.rows) ? res.data.rows : [],
        summary: res.data?.summary || null,
        truncated: !!res.data?.truncated,
      });
    } catch {
      toast.error('فشل تحميل التقرير');
    } finally {
      setLoading(false);
    }
  }, [companyId, config.endpoint, range.dateFrom, range.dateTo, branchId, costMethod, invoiceType, stockId, product, customer, f]);

  useEffect(() => {
    if (!ready) return undefined;
    const id = setTimeout(fetchReport, 250);
    return () => clearTimeout(id);
  }, [ready, fetchReport]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = applyProfitFilter(data.rows, profitFilter);
    return q ? rows.filter(r => config.search(r).toLowerCase().includes(q)) : rows;
  }, [data.rows, profitFilter, search, config]);

  // إجماليات المعروض (تتأثر بالبحث وفلتر الربح)
  const totals = useMemo(() => {
    const t = visibleRows.reduce((a, r) => ({
      revenue: a.revenue + (Number(r.revenue) || 0),
      cost: a.cost + (Number(r.cost) || 0),
    }), { revenue: 0, cost: 0 });
    const profit = t.revenue - t.cost;
    return { ...t, profit, margin: t.revenue ? (profit / t.revenue) * 100 : 0 };
  }, [visibleRows]);

  const s = data.summary;
  const warnCount = (s?.negativeStockLines || 0) + (s?.noCostLines || 0) + (s?.fallbackCostLines || 0);

  const exportExcel = () => {
    if (!visibleRows.length) return toast.info('لا توجد بيانات للتصدير');
    const rows = visibleRows.map(config.excel);
    rows.push({ [Object.keys(rows[0])[0]]: 'الإجمالي', 'الإيراد': totals.revenue, 'التكلفة': totals.cost, 'الربح': totals.profit, 'الهامش %': Number(totals.margin.toFixed(2)) });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!views'] = [{ RTL: true }];
    ws['!cols'] = Object.keys(rows[0]).map(k => ({ wch: ['الصنف', 'العميل'].includes(k) ? 30 : 14 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, config.title.slice(0, 30));
    XLSX.writeFile(wb, `${config.key}_${range.dateFrom}_${range.dateTo}.xlsx`);
  };

  const resetFilters = () => {
    range.reset();
    setBranchId(defaultBranch);
    setCostMethod('avg');
    setInvoiceType('');
    setStockId('');
    setProduct(null);
    setCustomer(null);
    setProfitFilter('');
    setSearch('');
  };

  const activeCount = [
    branchId && branchId !== defaultBranch, costMethod !== 'avg', invoiceType, stockId, product, customer, profitFilter, search.trim(),
  ].filter(Boolean).length;

  const bName = branchName(branchId);

  return (
    <ReportPage
      title={config.title}
      subtitle={config.subtitle}
      icon={config.icon}
      period={range}
      badges={[
        ...(bName ? [{ label: bName, icon: <StorefrontOutlinedIcon /> }] : []),
        { label: `التكلفة: ${costMethodLabel(costMethod)}`, icon: <PriceCheckOutlinedIcon /> },
      ]}
      actions={[
        { label: 'تحديث', icon: <RefreshIcon />, onClick: fetchReport, disabled: loading },
        { label: 'Excel', icon: <GridOnIcon />, onClick: exportExcel, primary: true },
      ]}
      stats={[
        { label: 'الإيراد (بدون ضريبة)', value: fmtMoney(totals.revenue), icon: <AttachMoneyIcon />, color: 'info' },
        { label: 'تكلفة المبيعات', value: fmtMoney(totals.cost), icon: <PriceCheckOutlinedIcon />, color: 'warning' },
        { label: 'هامش الربح', value: `${totals.margin.toFixed(1)}%`, icon: <PercentIcon />, color: totals.margin < 0 ? 'error' : 'primary' },
        { label: 'تنبيهات التكلفة', value: fmtNumber(warnCount), icon: <WarningAmberRoundedIcon />, color: warnCount ? 'warning' : 'slate' },
        { label: totals.profit < 0 ? 'صافي الخسارة' : 'صافي الربح', value: fmtMoney(totals.profit), icon: <SavingsOutlinedIcon />, color: totals.profit < 0 ? 'error' : 'success', highlight: true },
      ]}
      filters={
        <ReportFilters
          range={range}
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={f.product ? 'الصنف أو الفاتورة أو العميل...' : 'رقم الفاتورة أو العميل...'}
          onApply={fetchReport}
          onReset={resetFilters}
          loading={loading}
          activeCount={activeCount}
        >
          <ReportSelect label="الفرع" value={branchId} onChange={setBranchId} options={branchOptions}
            allLabel={defaultBranch ? null : 'كل الفروع'} />
          <ReportSelect label="طريقة التكلفة" value={costMethod} onChange={v => setCostMethod(v || 'avg')} options={COST_METHODS} allLabel={null} />
          {f.invoiceType && <ReportSelect label="نوع الفاتورة" value={invoiceType} onChange={setInvoiceType} options={INVOICE_TYPES} />}
          {f.stock && (
            <ReportSelect label="المخزن" value={stockId} onChange={setStockId}
              options={stocks.map(st => ({ value: String(st.id), label: st.nameAr || st.name }))} />
          )}
          {f.product && (
            <Autocomplete
              size="small" options={products} value={product} onChange={(_, v) => setProduct(v)}
              getOptionLabel={(p) => p?.arabicName || p?.nameAr || p?.name || ''}
              isOptionEqualToValue={(a, b) => (a.productId ?? a.id) === (b.productId ?? b.id)}
              noOptionsText="لا توجد نتائج"
              renderInput={(params) => <TextField {...params} label="الصنف" placeholder="الكل" sx={fieldSx} />}
            />
          )}
          {f.customer && (
            <Autocomplete
              size="small" options={customers} value={customer} onChange={(_, v) => setCustomer(v)}
              getOptionLabel={(c) => c?.name || ''}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              noOptionsText="لا توجد نتائج"
              renderInput={(params) => <TextField {...params} label="العميل" placeholder="الكل" sx={fieldSx} />}
            />
          )}
          <ReportSelect label="عرض" value={profitFilter} onChange={setProfitFilter} options={PROFIT_FILTERS} allLabel="الكل" />
        </ReportFilters>
      }
    >
      <CostingInfo method={costMethod} summary={s} />

      {data.truncated && (
        <Alert severity="warning" sx={{ mb: 1.5, borderRadius: '12px' }}>
          النتائج كثيرة وتم عرض جزء منها فقط — ضيّق الفترة أو اختر فرعًا/صنفًا.
        </Alert>
      )}

      <ReportTableCard title={config.gridTitle} count={fmtNumber(visibleRows.length)}>
        <DataGrid
          rows={visibleRows}
          columns={config.columns}
          loading={loading}
          getRowId={(r) => r.id}
          slots={{ toolbar: GridToolbar }}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          getRowHeight={() => 52}
          getRowClassName={(p) => (Number(p.row.profit) < 0 && p.row.invoiceType !== 3 ? 'row-loss' : '')}
          localeText={{
            noRowsLabel: config.noRows,
            toolbarColumns: 'الأعمدة',
            toolbarFilters: 'الفلاتر',
            toolbarDensity: 'الكثافة',
            toolbarExport: 'تصدير',
          }}
          sx={{ ...reportGridSx, '& .row-loss': { bgcolor: '#fff1f2 !important' } }}
        />
      </ReportTableCard>
    </ReportPage>
  );
}
